import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';

import { default as Radio } from '@dojo/widgets/radio';
import { Checkbox } from '@dojo/widgets/checkbox';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { userstate, createNlsMiddleware, contentOrderChange, recommended } from '@holmescorp/lcc-state';

import css from '../css/explore-view.m.css';
import { resourceDitabookSelector } from '@holmescorp/resource-parser';

interface ContentOrRecommendedIcache {
	recommended: boolean;
	hide_completed: boolean;
}

export interface OrderSelectionProperties {
	initialSelection: 'content' | 'recommended' | undefined;
	shouldHideCompleted: (hide: boolean) => void;
	showOrderView: (order: 'content' | 'recommended') => void;
}

const icache = createICacheMiddleware<ContentOrRecommendedIcache>();
const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	settings,
	userstate,
	recommended
}).properties<OrderSelectionProperties>();

export const ContentOrRecommended = factory(
	({ properties, middleware: { icache, theme, nls, settings, userstate, recommended } }) => {
		const themeCss = theme.classes(css);
		const {
			messages: {
				contentswitch_on = '[ditabook] contentswitch_on',
				contentswitch_off = '[ditabook] contentswitch_off',
				hide_completed = '[ditabook] hide_completed',
				// book_order = '[ditabook] book_order',
				choosePath = '[ditabook] choosePath'
				// recommended_order = '[ditabook] recommended_order'
			}
			// format
		} = nls();

		const { initialSelection = 'content', shouldHideCompleted, showOrderView } = properties();
		const hasAssess = recommended.hasAssessment();
		const { get, path } = userstate;
		const contentorder = get(path('toolstate', 'contentorder')) || { extra: initialSelection, hide: true };
		let showRecommended = icache.getOrSet(
			'recommended',
			contentorder.extra ? contentorder.extra === 'recommended' : hasAssess
		);
		const hideCompleted = icache.getOrSet('hide_completed', !!contentorder.hide);
		// handle quiz path switch
		if (!hasAssess && showRecommended) {
			icache.set('recommended', false);
			showRecommended = false;
			contentOrderChange({ ...contentorder, extra: 'content' });
			showOrderView('content');
		}

		const { messages: settingsMessages } = settings();
		const quizpath = get(path('quizPattern'));
		const pathid = quizpath && quizpath.length > 0 ? settingsMessages[`path-${quizpath}`] || '' : '';

		const recommendedRadio = v(
			'div',
			{ classes: [themeCss.contentSelectorGroup], xqa: 'select-recommended-order' },
			[
				w(
					Radio,
					{
						checked: showRecommended,
						disabled: !hasAssess,
						onValue: (checked: boolean) => {
							if (hasAssess && checked) {
								icache.set('recommended', true);
								contentOrderChange({ ...contentorder, extra: 'recommended' });
								showOrderView('recommended');
							}
						}
					},
					[contentswitch_on]
				)
			]
		);
		const contentRadio = v('div', { classes: [themeCss.contentSelectorGroup], xqa: 'select-content-order' }, [
			w(
				Radio,
				{
					checked: !showRecommended,
					disabled: !hasAssess,
					onValue: (checked: boolean) => {
						if (hasAssess && checked) {
							icache.set('recommended', false);
							contentOrderChange({ ...contentorder, extra: 'content' });
							showOrderView('content');
						}
					}
				},
				[contentswitch_off]
			)
		]);

		const legend = v('legend', { classes: [themeCss.visuallyHidden] }, [choosePath]);

		return v(
			'div',
			{
				key: 'order-block',
				classes: [
					themeCss.contentOrRecommended,
					showRecommended ? themeCss.recommendedSelected : themeCss.contentSelected
				]
			},
			[
				v('div', {}, [
					v('fieldset', { classes: [themeCss.fieldset] }, [legend, contentRadio, recommendedRadio])
				]),
				showRecommended
					? w(
							Checkbox,
							{
								checked: hideCompleted,
								onValue: (checked) => {
									icache.set('hide_completed', checked);
									contentOrderChange({ ...contentorder, hide: checked });
									shouldHideCompleted(checked);
								}
							},
							[hide_completed]
					  )
					: v('div', { key: 'content-check-dummy' }, []),
				v('div', { key: 'order', classes: [themeCss.viewNote] }, [
					// showRecommended ? format('recommended_order', { pathid }) : book_order
					w(resourceDitabookSelector, {
						topic: showRecommended ? 'recommended_order' : 'book_order',
						key: 'study-invite',
						classes: [themeCss.about],
						formatting: showRecommended ? { pathid } : {}
					})
				])
			]
		);
	}
);
